/**
 * Browser-side image compressor — preprocesses uploads BEFORE they hit
 * the network so users on slow connections (mobile data, African ISPs)
 * don't waste bandwidth shipping a 4MB original phone photo.
 *
 * Pipeline:
 *   1. Read the source file into an HTMLImageElement.
 *   2. Auto-rotate via `createImageBitmap` (browser handles EXIF).
 *   3. Downscale so the longest edge is ≤ maxDimension (default 1200px),
 *      preserving aspect ratio.
 *   4. Encode as WebP at the requested quality (default 0.82) using
 *      OffscreenCanvas where available, regular canvas otherwise.
 *   5. Return a fresh File object — same name, .webp extension.
 *
 * Falls back to the original file on any failure (browsers without
 * canvas.toBlob('image/webp'), out-of-memory, etc.) so the upload
 * still succeeds end-to-end. The backend will re-validate and resize
 * regardless.
 */
const DEFAULT_MAX_DIMENSION = 1200;
const DEFAULT_QUALITY = 0.82;
const SKIP_THRESHOLD_BYTES = 80 * 1024; // < 80KB: not worth re-encoding

const supportsWebP = (() => {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('image/webp') === 5;
  } catch {
    return false;
  }
})();

async function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall through to <img> fallback (older Safari).
    }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function targetSize(width, height, maxDim) {
  if (Math.max(width, height) <= maxDim) return { w: width, h: height };
  if (width >= height) {
    return { w: maxDim, h: Math.round(height * (maxDim / width)) };
  }
  return { w: Math.round(width * (maxDim / height)), h: maxDim };
}

export async function compressImageInBrowser(file, opts = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  // Animated/SVG/HEIC: not safely re-encodable in canvas — pass through.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;
  // Already small: skip work.
  if (file.size <= SKIP_THRESHOLD_BYTES) return file;
  if (!supportsWebP) return file;

  const maxDim = opts.maxDimension || DEFAULT_MAX_DIMENSION;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  try {
    const src = await loadBitmap(file);
    const srcW = src.width || src.naturalWidth;
    const srcH = src.height || src.naturalHeight;
    const { w, h } = targetSize(srcW, srcH, maxDim);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/webp', quality),
    );
    if (!blob) return file;

    // If WebP came out *bigger* than original (rare, e.g. tiny PNG line art),
    // keep the original.
    if (blob.size >= file.size) return file;

    const baseName = (file.name || 'upload').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  } catch (err) {
    // Silent fallback — backend will re-validate and optimize anyway.
    return file;
  }
}
